import { useCallback, useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Search, Pencil, Trash2, Plus } from "lucide-react";

const FIELDS = [
  { key: "date", label: "Date", hints: ["date", "purchase", "order date"] },
  { key: "name", label: "Name", hints: ["name", "customer"] },
  { key: "phone", label: "Phone Number", hints: ["phone", "mobile", "contact", "whatsapp"] },
  { key: "email", label: "Email ID", hints: ["email", "mail"] },
  { key: "product_name", label: "Product Name", hints: ["product", "item", "sku"] },
  { key: "pin_code", label: "Pin Code", hints: ["pin", "zip", "postal"] },
  { key: "city", label: "City", hints: ["city", "town", "location"] },
  { key: "order_status", label: "Order Status", hints: ["status"] },
];

const inputCls = "bg-slate-900 border-slate-700 text-slate-100";

function CustomerForm({ initial, onSave, saving, lockContacts }) {
  const [form, setForm] = useState(initial);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map((f) => {
          const locked = lockContacts && (f.key === "email" || f.key === "phone");
          return (
            <div key={f.key} className="space-y-1">
              <Label className="text-slate-300 text-xs">{f.label}{locked && <span className="text-amber-400 ml-1">(hidden)</span>}</Label>
              <Input data-testid={`customer-form-${f.key}`} disabled={locked} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={`${inputCls} ${locked ? "opacity-50" : ""}`} placeholder={f.key === "date" ? "YYYY-MM-DD or DD/MM/YYYY" : ""} />
            </div>
          );
        })}
      </div>
      {"unsubscribed" in initial && (
        <div className="flex gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input data-testid="customer-form-unsubscribed" type="checkbox" checked={!!form.unsubscribed} onChange={(e) => setForm({ ...form, unsubscribed: e.target.checked })} />
            Email unsubscribed
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input data-testid="customer-form-wa-opted-out" type="checkbox" checked={!!form.wa_opted_out} onChange={(e) => setForm({ ...form, wa_opted_out: e.target.checked })} />
            WhatsApp opted out
          </label>
        </div>
      )}
      <Button data-testid="customer-form-save" disabled={saving} onClick={() => onSave(form)} className="bg-indigo-500 hover:bg-indigo-600 text-white w-full">
        {saving ? "Saving…" : "Save customer"}
      </Button>
    </div>
  );
}

function ImportDialog({ open, onClose, onImported }) {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const handleFile = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const hdrs = res.meta.fields || [];
        setHeaders(hdrs);
        setRows(res.data);
        const m = {};
        FIELDS.forEach((f) => {
          const found = hdrs.find((h) => f.hints.some((hint) => h.toLowerCase().includes(hint)));
          if (found) m[f.key] = found;
        });
        setMapping(m);
      },
      error: () => toast.error("Could not parse the CSV file"),
    });
  };

  const mapped = rows.slice(0, 5).map((r) => Object.fromEntries(FIELDS.map((f) => [f.key, mapping[f.key] ? r[mapping[f.key]] : ""])));

  const doImport = async () => {
    setImporting(true);
    try {
      const payload = rows.map((r) => Object.fromEntries(FIELDS.map((f) => [f.key, mapping[f.key] ? r[mapping[f.key]] : ""])));
      const { data } = await api.post("/customers/import", { rows: payload });
      toast.success(`Imported ${data.imported} records (${data.skipped} skipped as duplicates/empty)`);
      onImported();
      onClose();
      setRows([]); setHeaders([]);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Import customers from CSV</DialogTitle></DialogHeader>
        {!headers.length ? (
          <div
            className="border-2 border-dashed border-slate-700 rounded-xl p-10 text-center cursor-pointer hover:border-indigo-500/60 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="csv-dropzone"
          >
            <Upload size={28} className="mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-300 mb-1">Click to choose a CSV file</p>
            <p className="text-xs text-slate-500">Header row required. Columns: Date, Name, Phone, Email, Product, Pin Code, City</p>
            <input ref={fileRef} data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-2">Map columns · {rows.length} rows found</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-slate-400 text-xs">{f.label}</Label>
                    <Select value={mapping[f.key] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === "__none__" ? undefined : v })}>
                      <SelectTrigger data-testid={`csv-map-${f.key}`} className="bg-slate-900 border-slate-700 text-slate-200 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                        <SelectItem value="__none__">— skip —</SelectItem>
                        {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-2">Preview (first 5)</p>
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <Table>
                  <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">{FIELDS.map((f) => <TableHead key={f.key} className="text-slate-400 text-xs">{f.label}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {mapped.map((r, i) => (
                      <TableRow key={i} className="border-slate-800 hover:bg-slate-800/40">
                        {FIELDS.map((f) => <TableCell key={f.key} className="text-slate-300 text-xs whitespace-nowrap">{r[f.key] || <span className="text-slate-600">—</span>}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="flex gap-3">
              <Button data-testid="csv-import-confirm" disabled={importing} onClick={doImport} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                {importing ? "Importing…" : `Import ${rows.length} rows`}
              </Button>
              <Button variant="outline" onClick={() => { setRows([]); setHeaders([]); }} className="border-slate-700 text-slate-300">Choose another file</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CustomersPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/customers", { params: { search, page, limit: 25 } }).then((r) => setData(r.data)).catch(() => {});
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Customer updated" : "Customer added");
      setEditing(null); setAdding(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    load();
  };

  const pages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1">Purchase-level records</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Customers</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-customer-button" variant="outline" onClick={() => setAdding(true)} className="border-slate-700 text-slate-200 hover:bg-slate-800">
            <Plus size={15} className="mr-1.5" /> Add
          </Button>
          <Button data-testid="csv-upload-button" onClick={() => setImportOpen(true)} className="bg-indigo-500 hover:bg-indigo-600 text-white">
            <Upload size={15} className="mr-1.5" /> Import CSV
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input data-testid="customer-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, email, phone, product, city…" className="pl-9 bg-slate-900 border-slate-700 text-slate-100" />
      </div>

      <div className="bg-[#111827] border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {["Date", "Name", "Phone", "Email", "Product", "Pin", "City", "Status", "Flags", ""].map((h) => (
                  <TableHead key={h} className="text-slate-400 text-xs whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 ? (
                <TableRow className="border-slate-800"><TableCell colSpan={10} className="text-center text-slate-500 py-10" data-testid="customers-empty-state">No customers yet. Import a CSV or add one manually.</TableCell></TableRow>
              ) : data.items.map((c) => (
                <TableRow key={c.id} data-testid="customer-row" className="border-slate-800 hover:bg-slate-800/40">
                  <TableCell className="text-slate-300 text-xs font-mono whitespace-nowrap">{c.date || "—"}</TableCell>
                  <TableCell className="text-slate-200 text-sm whitespace-nowrap">{c.name || "—"}</TableCell>
                  <TableCell className="text-slate-300 text-xs font-mono whitespace-nowrap">{c.phone || "—"}</TableCell>
                  <TableCell className="text-slate-300 text-xs whitespace-nowrap">{c.email || "—"}</TableCell>
                  <TableCell className="text-slate-300 text-xs whitespace-nowrap max-w-[180px] truncate">{c.product_name || "—"}</TableCell>
                  <TableCell className="text-slate-300 text-xs font-mono">{c.pin_code || "—"}</TableCell>
                  <TableCell className="text-slate-300 text-xs whitespace-nowrap">{c.city || "—"}</TableCell>
                  <TableCell className="text-slate-400 text-xs whitespace-nowrap">{c.order_status || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {c.unsubscribed && <Badge className="bg-rose-500/15 text-rose-400 border border-rose-500/30 mr-1 text-[10px]">unsub</Badge>}
                    {c.wa_opted_out && <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[10px]">wa-out</Badge>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <button data-testid="customer-edit-button" onClick={() => setEditing(c)} className="text-slate-400 hover:text-indigo-400 p-1"><Pencil size={14} /></button>
                    <button data-testid="customer-delete-button" onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-400 p-1"><Trash2 size={14} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
          <span className="text-xs text-slate-500 font-mono" data-testid="customers-total">{data.total} records</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-slate-700 text-slate-300 h-7 px-2" data-testid="customers-prev-page">Prev</Button>
            <span className="text-xs text-slate-400 font-mono">{page}/{pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-slate-700 text-slate-300 h-7 px-2" data-testid="customers-next-page">Next</Button>
          </div>
        </div>
      </div>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />

      <Dialog open={!!editing || adding} onOpenChange={(v) => { if (!v) { setEditing(null); setAdding(false); } }}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit customer" : "Add customer"}</DialogTitle></DialogHeader>
          <CustomerForm
            key={editing?.id || "new"}
            initial={editing || { date: "", name: "", phone: "", email: "", product_name: "", pin_code: "", city: "" }}
            onSave={save}
            saving={saving}
            lockContacts={!!editing && user?.role === "agent"}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
